import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { CollegeLogo } from '../../components/CollegeSearchCombobox';
import '../../college-theme.css';

const CollegeGlobe = React.lazy(() => import('../../components/college/CollegeGlobe'));

/* ─── Constants ──────────────────────────────────────────────────────── */

const PLAN_LABELS = {
  ED: 'Early Decision',
  ED_II: 'Early Decision II',
  EA: 'Early Action',
  REA: 'Restrictive Early Action',
  RD: 'Regular Decision',
  ROLLING: 'Rolling',
  UCAS: 'UCAS Main',
  OTHER: 'Other',
};

const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

// Real per-school application dates aren't tracked at the Browse level — this
// is flavor text only, illustrating the typical calendar for each round.
const TYPICAL_DATES = {
  ED: 'Nov 1',
  EA: 'Nov 1',
  REA: 'Nov 1',
  ED_II: 'Jan 2',
  RD: 'Jan 1–15',
  ROLLING: 'rolling admission',
  UCAS: 'Jan 15',
  OTHER: 'varies',
};

/* ─── Helpers ────────────────────────────────────────────────────────── */

function nextJan1() {
  const year = new Date().getFullYear() + 1;
  return `${year}-01-01`;
}

const fmtPercent = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const fmtCount = (v) => (v == null ? '—' : Number(v).toLocaleString());
function fmtSatRange(sat25, sat75) {
  if (sat25 == null && sat75 == null) return '—';
  if (sat25 == null) return `≤${sat75}`;
  if (sat75 == null) return `${sat25}+`;
  return `${sat25}–${sat75}`;
}

/* ─── Small presentational pieces ───────────────────────────────────── */

function PlanChip({ plan }) {
  const solid = plan === 'ED';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '4px 11px',
        borderRadius: 999,
        border: '1px solid var(--college-ink)',
        background: solid ? 'var(--college-ink)' : 'transparent',
        color: solid ? 'var(--college-panel)' : 'var(--college-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      {PLAN_SHORT[plan] || plan}
    </span>
  );
}

function FitBadge({ fit, fitColor, fitBg }) {
  if (!fit) {
    return <span style={{ fontSize: 11, color: 'var(--college-muted-2)' }}>Not enough data yet</span>;
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 12px',
        borderRadius: 999,
        background: fitBg,
        color: fitColor,
        whiteSpace: 'nowrap',
      }}
    >
      {fit}
    </span>
  );
}

function StatCell({ label, value }) {
  return (
    <div style={{ background: 'var(--college-panel)', padding: 16 }}>
      <p className="label-editorial" style={{ margin: '0 0 6px', fontSize: 10 }}>{label}</p>
      <p className="font-mono-editorial" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--college-ink)' }}>
        {value}
      </p>
    </div>
  );
}

function PhotoPlaceholder({ label, style }) {
  return (
    <div
      style={{
        background: 'var(--college-bg-alt)', border: '1px solid var(--college-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', ...style,
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--college-muted)', letterSpacing: '0.04em' }}>{label}</span>
    </div>
  );
}

const primaryButtonStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--college-ink)',
  color: 'var(--college-panel)', border: 'none', padding: '11px 22px', fontSize: 13,
  fontWeight: 600, letterSpacing: '0.02em', cursor: 'pointer', whiteSpace: 'nowrap',
};

const outlineGreenButtonStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent',
  color: '#3E6B44', border: '1px solid #3E6B44', padding: '11px 22px', fontSize: 13,
  fontWeight: 600, letterSpacing: '0.02em', cursor: 'pointer', whiteSpace: 'nowrap',
};

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function CollegeBrowseDetailPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [college, setCollege] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    api.get(`/college-browse/${id}`)
      .then((res) => {
        if (!alive) return;
        setCollege(res.data.college);
        setLinks(res.data.links || []);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err.response?.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!college) return;
    setAdding(true);
    try {
      const res = await api.post('/college-applications', {
        collegeName: college.name,
        domain: college.domain,
        schoolUrl: `https://${college.domain}`,
        city: college.city,
        country: 'US',
        lat: college.lat,
        lng: college.lng,
        plan: 'RD',
        planLabel: 'Regular Decision',
        deadline: nextJan1(),
        scorecardData: {
          admissionRate: college.admissionRate,
          satComposite75: college.sat75,
          satMath25: null,
          satReading25: null,
        },
      });
      toast.success('Added — retag the plan and deadline from My List');
      navigate(`/college/${res.data.application.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add school');
    } finally {
      setAdding(false);
    }
  };

  const backLink = (
    <Link
      to="/college/browse"
      style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--college-muted)', textDecoration: 'none' }}
    >
      ← Browse
    </Link>
  );

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        {backLink}
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)', marginTop: 24 }}>
          Loading…
        </p>
      </div>
    );
  }

  if (loadError || !college) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        {backLink}
        <p className="font-serif-editorial" style={{ fontSize: 32, fontStyle: 'italic', margin: '24px 0 8px' }}>
          {loadError === 'not-found' ? "This college couldn't be found" : 'Something went wrong loading this college'}
        </p>
        <Link to="/college/browse" style={{ fontSize: 13, color: 'var(--college-ink)', textDecoration: 'underline' }}>
          Back to Browse →
        </Link>
      </div>
    );
  }

  const hasPin = college.lat != null && college.lng != null;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
      <style>{`
        @media (max-width: 900px) {
          .college-browse-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {backLink}

      <div style={{ marginTop: 24 }} className="animate-fade-up">
        <p className="label-editorial" style={{ margin: '0 0 10px' }}>
          US News #{college.rank} · {college.type || '—'} · {college.city || '—'}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <CollegeLogo schoolUrl={`https://${college.domain}`} name={college.name} size={44} />
            <h1
              className="font-serif-editorial"
              style={{ fontSize: 54, fontStyle: 'italic', fontWeight: 400, margin: 0, lineHeight: 1.05 }}
            >
              {college.name}
            </h1>
            <FitBadge fit={college.fit} fitColor={college.fitColor} fitBg={college.fitBg} />
          </div>

          {college.onList ? (
            <button
              type="button"
              onClick={() => navigate(`/college/${college.applicationId}`)}
              style={outlineGreenButtonStyle}
            >
              ✓ On list — open tracker
            </button>
          ) : (
            <button type="button" onClick={handleAdd} disabled={adding} style={{ ...primaryButtonStyle, opacity: adding ? 0.6 : 1 }}>
              {adding ? 'Adding…' : '+ Add to My List'}
            </button>
          )}
        </div>
      </div>

      <div
        className="college-browse-detail-grid animate-fade-up"
        style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 48, marginTop: 40, alignItems: 'start' }}
      >
        {/* ─── LEFT COLUMN ─────────────────────────────────── */}
        <div>
          {/* Photo placeholder collage */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginBottom: 6 }}>
            <PhotoPlaceholder label="Campus photo" style={{ height: 220 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 36 }}>
            <PhotoPlaceholder label="Campus photo" style={{ height: 130 }} />
            <PhotoPlaceholder label="Campus photo" style={{ height: 130 }} />
          </div>

          {/* Stats grid */}
          <div
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
              background: 'var(--college-border)', marginBottom: 40,
            }}
          >
            <StatCell label="Admit rate" value={fmtPercent(college.admissionRate)} />
            <StatCell label="SAT mid 50%" value={fmtSatRange(college.sat25, college.sat75)} />
            <StatCell label="Cost / yr" value={college.costShort || '—'} />
            <StatCell label="Undergrads" value={fmtCount(college.enrollment)} />
          </div>

          {/* Plans offered */}
          <p className="label-editorial" style={{ margin: '0 0 14px' }}>Application plans offered</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {(college.plans || []).map((plan) => (
              <div
                key={plan}
                style={{
                  border: '1px solid var(--college-ink)', background: 'var(--college-panel)',
                  padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <PlanChip plan={plan} />
                <span style={{ fontSize: 13, color: 'var(--college-ink-soft)' }}>
                  {PLAN_LABELS[plan] || plan} — <span className="font-mono-editorial">{TYPICAL_DATES[plan] || 'varies'}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ─── RIGHT RAIL ──────────────────────────────────── */}
        <div>
          <div style={{ background: 'var(--college-ink)', padding: 20 }}>
            {hasPin ? (
              <React.Suspense fallback={<div style={{ height: 360, background: 'rgba(241,233,216,0.05)' }} />}>
                <CollegeGlobe
                  singlePin={{ lat: college.lat, lng: college.lng, label: college.name, color: '#D8A94C' }}
                  height={360}
                />
              </React.Suspense>
            ) : (
              <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 12, color: 'rgba(241,233,216,0.45)' }}>Location unavailable</span>
              </div>
            )}
          </div>
          <p style={{ textAlign: 'center', margin: '10px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
            {college.city || 'Campus'} · drag to spin
          </p>

          <div style={{ marginTop: 40 }}>
            <p className="label-editorial" style={{ margin: '0 0 14px' }}>Links</p>
            <div>
              {links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    padding: '11px 0', borderBottom: '1px solid var(--college-border-soft)',
                    textDecoration: 'none', color: 'var(--college-ink)',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{link.label}</span>
                  <span
                    className="font-mono-editorial"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--college-muted)' }}
                  >
                    {link.host}
                    <ExternalLink size={12} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
